import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

function getInitialSavedCount() {
  try {
    const match = document.cookie.match(/uscope_saved=([^;]*)/);
    return match ? match[1].split(',').filter(Boolean).length : 0;
  } catch { return 0; }
}

const MENU_ITEMS = [
  { label: 'Home',             path: '/' },
  { label: 'Browse categories', path: '/category/ecommerce' },
  { label: 'Trends',           path: '/trends' },
  { label: 'Claude Skills',    path: '/skills' },
  { label: 'Submit a Website', path: '/submit' },
  { label: 'Contact us',        path: '/contact' },
];

export default function Nav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navSearchRef = useRef(null);
  const flyingURef = useRef(null);
  const wordmarkURef = useRef(null);
  const iconDestRef = useRef(null);

  const isHome = location.pathname === '/';
  const [savedCount, setSavedCount] = useState(getInitialSavedCount);

  // Listen for save/unsave events from SaveButton to update badge without page reload
  useEffect(() => {
    function onSavedChanged(e) {
      setSavedCount(e.detail?.count ?? getInitialSavedCount());
    }
    window.addEventListener('uscope:saved_changed', onSavedChanged);
    return () => window.removeEventListener('uscope:saved_changed', onSavedChanged);
  }, []);

  // Show split state only when hero search is scrolled out of view.
  // On home: wait up to ~500ms for #heroSearchInput to appear (it renders
  // inside Homepage which mounts after Nav). On other pages: scrolled=true.
  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }

    // Start optimistic: hero is visible until proven otherwise
    setScrolled(false);

    let observer;
    let attempts = 0;

    function attach() {
      const target = document.getElementById('heroSearchInput');
      if (!target) {
        // Not in DOM yet — retry up to 10 times (~500ms total)
        if (attempts++ < 10) {
          setTimeout(attach, 50);
        }
        return;
      }

      // Correct initial state based on current scroll position
      const rect = target.getBoundingClientRect();
      const navH = 52;
      const isVisible = rect.bottom > navH && rect.top < window.innerHeight;
      setScrolled(!isVisible);

      observer = new IntersectionObserver(
        ([entry]) => setScrolled(!entry.isIntersecting),
        { threshold: 0, rootMargin: '-52px 0px 0px 0px' }
      );
      observer.observe(target);
    }

    attach();
    return () => { observer && observer.disconnect(); };
  }, [location.pathname, isHome]);

  // Sync nav search ↔ hero search
  useEffect(() => {
    const heroInput = document.getElementById('heroSearchInput');
    const navInput = navSearchRef.current;
    if (!heroInput || !navInput) return;

    const syncToNav = () => { navInput.value = heroInput.value; };
    const syncToHero = () => { heroInput.value = navInput.value; };

    heroInput.addEventListener('input', syncToNav);
    navInput.addEventListener('input', syncToHero);
    return () => {
      heroInput.removeEventListener('input', syncToNav);
      navInput.removeEventListener('input', syncToHero);
    };
  }, [scrolled]);

  // Measure wordmark-u center → icon-dest center, write to flying element as CSS vars
  useLayoutEffect(() => {
    if (!isHome) return;

    function measure() {
      const uEl = wordmarkURef.current;
      const destEl = iconDestRef.current;
      const flyEl = flyingURef.current;
      if (!uEl || !destEl || !flyEl) return;

      const uRect = uEl.getBoundingClientRect();
      const destRect = destEl.getBoundingClientRect();

      // Origin: center of the "u" in the wordmark
      const originX = uRect.left + uRect.width / 2;
      const originY = uRect.top + uRect.height / 2;

      // Destination: center of the icon box
      const destX = destRect.left + destRect.width / 2;
      const destY = destRect.top + destRect.height / 2;

      flyEl.style.setProperty('--fly-origin-x', `${originX}px`);
      flyEl.style.setProperty('--fly-origin-y', `${originY}px`);
      flyEl.style.setProperty('--fly-dx', `${destX - originX}px`);
      flyEl.style.setProperty('--fly-dy', `${destY - originY}px`);
    }

    measure();
    // Re-measure when scrolled changes so destination coords are always fresh
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [isHome, scrolled]);

  function handleNavSearch() {
    const val = navSearchRef.current?.value.trim();
    if (!val) return;
    const heroInput = document.getElementById('heroSearchInput');
    if (heroInput) {
      heroInput.value = val;
      heroInput.dispatchEvent(new Event('input', { bubbles: true }));
      heroInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
    } else {
      navigate(`/search?q=${encodeURIComponent(val)}`);
    }
  }

  const bar1 = { ...styles.bar, transform: menuOpen ? 'translateY(3.75px) rotate(45deg)' : 'none' };
  const bar2 = { ...styles.bar, transform: menuOpen ? 'translateY(-3.75px) rotate(-45deg)' : 'none' };

  return (
    <>
      <header
        id="nav"
        className={scrolled ? 'scrolled' : ''}
        style={{ ...styles.header, background: 'rgba(20,20,19,0.6)' }}
      >
        <div style={styles.inner}>
          {/* Left — static logo on non-home; icon destination placeholder on home */}
          <div style={styles.navLeft}>
            {!isHome && (
              <div className="nav-logo-wrap nav-logo-static" onClick={() => navigate('/')}>
                <span className="logo-icon" aria-hidden="true">
                  <span className="logo-icon-box">
                    <span className="logo-icon-text">
                      <span className="logo-icon-u">u</span>
                      <span className="logo-icon-s">S</span>
                    </span>
                  </span>
                </span>
              </div>
            )}
            {/* Invisible spacer — in DOM for JS measurement only, never visible */}
            {isHome && (
              <div
                ref={iconDestRef}
                className="nsp-icon-dest"
                aria-hidden="true"
              />
            )}
          </div>

          {/* Home: split animation */}
          {isHome && (
            <div className={`nav-split${scrolled ? ' is-scrolled' : ''}`}>
              {/* Layer 1: wordmark — "u" is invisible (flying element covers it) */}
              <div className="nsp-wordmark" onClick={() => navigate('/')}>
                <span ref={wordmarkURef} className="nsp-wordmark-u nsp-wordmark-u-src">u</span>
                <span className="nsp-wordmark-scope">Scope</span>
              </div>
              {/* Layer 2: search pill — fades in when scrolled */}
              <div className="nsp-controls">
                <div className="nsp-pill">
                  <input
                    ref={navSearchRef}
                    type="search"
                    placeholder="Search uScope…"
                    autoComplete="off"
                    aria-label="Search uScope"
                    onKeyDown={e => { if (e.key === 'Enter') handleNavSearch(); }}
                    tabIndex={scrolled ? 0 : -1}
                  />
                  <button onClick={handleNavSearch} aria-label="Search">
                    <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" width="14" height="14">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Non-home: centered nav search */}
          {!isHome && (
            <div className="nav-search-scroll">
              <input
                ref={navSearchRef}
                type="search"
                placeholder="Search uScope…"
                autoComplete="off"
                aria-label="Search uScope"
                onKeyDown={e => { if (e.key === 'Enter') handleNavSearch(); }}
              />
              <button onClick={handleNavSearch} aria-label="Search" style={styles.navSearchBtn}>
                <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" width="14" height="14">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
            </div>
          )}

          {/* Right actions — bookmark + hamburger grouped together on the far right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
            <button
              onClick={() => navigate('/my-stack')}
              aria-label="My saved tools"
              style={{ ...styles.hamburger, position: 'relative', padding: '8px' }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              {savedCount > 0 && (
                <span style={{
                  position: 'absolute', top: 4, right: 4,
                  background: '#12B8A0', color: '#0e0e0e',
                  borderRadius: '50%', width: 14, height: 14,
                  fontSize: 9, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1,
                }}>
                  {savedCount > 9 ? '9+' : savedCount}
                </span>
              )}
            </button>

            <button
              className="nav-hamburger"
              style={styles.hamburger}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(v => !v)}
            >
              <span style={bar1} />
              <span style={bar2} />
            </button>
          </div>
        </div>
      </header>

      {/* Flying element — starts as "u" in wordmark, morphs into full uS box */}
      {isHome && (
        <div
          ref={flyingURef}
          className={`nsp-flying-u${scrolled ? ' nsp-flying-u-gone' : ''}`}
          onClick={() => navigate('/')}
        >
          <span className="nsp-fu-u">u</span>
          <span className="nsp-fu-s" aria-hidden="true">S</span>
        </div>
      )}

      {/* Slide-down menu panel */}
      <div style={{
        ...styles.dropdownWrap,
        maxHeight: menuOpen ? '400px' : '0',
        opacity: menuOpen ? 1 : 0,
        pointerEvents: menuOpen ? 'auto' : 'none',
      }}>
        <div style={styles.dropdownInner}>
          {MENU_ITEMS.map(({ label, path }, i) => (
            <button
              key={label}
              style={{
                ...styles.dropdownItem,
                transitionDelay: menuOpen ? `${i * 45}ms` : '0ms',
                opacity: menuOpen ? 1 : 0,
                transform: menuOpen ? 'translateY(0)' : 'translateY(-8px)',
              }}
              onClick={() => { navigate(path); setMenuOpen(false); }}
            >
              {label}
            </button>
          ))}
          <div style={styles.dropdownDivider} />
          <div style={{
            ...styles.dropdownActions,
            transitionDelay: menuOpen ? `${MENU_ITEMS.length * 45}ms` : '0ms',
            opacity: menuOpen ? 1 : 0,
            transform: menuOpen ? 'translateY(0)' : 'translateY(-8px)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
          }}>
            <button style={styles.menuBtnOutline} onClick={() => { navigate('/login'); setMenuOpen(false); }}>Log In</button>
            <button style={styles.menuBtnSolid} onClick={() => { navigate('/signup'); setMenuOpen(false); }}>Sign Up Free</button>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  header: {
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    transition: 'background 0.3s ease',
  },
  inner: {
    height: 'var(--nav-h)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 clamp(12px, 3vw, 24px)',
    position: 'relative',
  },
  navLeft: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    minWidth: '48px',
    maxWidth: 'clamp(120px, 30vw, 200px)',
  },
  hamburger: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '8px',
    flexShrink: 0,
  },
  bar: {
    display: 'block',
    width: '22px',
    height: '1.5px',
    background: 'rgba(255,255,255,0.75)',
    borderRadius: '2px',
    transition: 'transform 0.3s ease, opacity 0.3s ease',
    willChange: 'transform',
  },
  navSearchBtn: {
    background: 'none',
    border: 'none',
    padding: '7px 14px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  dropdownWrap: {
    position: 'fixed',
    top: 'var(--nav-h)',
    left: 0,
    right: 0,
    overflow: 'hidden',
    zIndex: 999,
    background: 'rgba(20,20,19,0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    transition: 'max-height 0.38s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease',
  },
  dropdownInner: {
    maxWidth: 'min(480px, 100vw)',
    margin: '0 auto',
    padding: '12px clamp(16px, 4vw, 24px) 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  dropdownItem: {
    fontFamily: 'var(--font-sans)',
    fontSize: '1rem',
    color: 'rgba(255,255,255,0.65)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '12px 0',
    textAlign: 'left',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    transition: 'color 0.15s ease, opacity 0.2s ease, transform 0.2s ease',
    letterSpacing: '-0.01em',
  },
  dropdownDivider: {
    height: '0',
    margin: '8px 0 4px',
  },
  dropdownActions: {
    display: 'flex',
    gap: '8px',
    padding: '4px 6px 6px',
  },
  menuBtnOutline: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(255,255,255,0.2)',
    padding: '10px 28px',
    borderRadius: '100px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  menuBtnSolid: {
    background: 'var(--teal)',
    color: '#fff',
    border: '1px solid transparent',
    padding: '10px 28px',
    borderRadius: '100px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
};
